import { ICONS } from '@constants'
import { useOnClickOutside } from '@hooks/use-click-outside'
import clsx from 'clsx'
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { AcInputBase } from '../ac-input-base/ac-input-base'
import { AcCard, AcIcon, AcTypography } from '../index.core.component'
import styles from './ac-autocomplete.module.scss'

type IAcAutocompleteOption = Record<string, string | number | null>

interface IAcAutocompleteProps {
  name: string
  className?: string
  id: string
  options: any[]
  valueKey: string
  labelKey: string
  loading?: boolean
  placeholder?: string
  disabled?: boolean
  hint?: string
  error?: string | boolean
  debounceMs?: number
  onChange: (newValue: any) => void
  onSearch: (query: string) => void
  renderOption?: (option: IAcAutocompleteOption) => ReactNode
}

export const AcAutocomplete: React.FC<IAcAutocompleteProps> = ({
  name,
  id,
  options,
  loading,
  disabled,
  hint,
  error,
  placeholder,
  valueKey,
  labelKey,
  renderOption,
  className,
  onChange,
  onSearch,
  debounceMs = 300,
}) => {
  const [showOptions, setShowOptions] = useState(false)
  const [query, setQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  useOnClickOutside(dropdownRef, () => handleClickOutside())

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Automatically open dropdown when options are available and there's a query
  useEffect(() => {
    if (query && options.length > 0 && !loading) {
      setShowOptions(true)
    }
  }, [options, query, loading])

  const handleClickOutside = () => {
    setShowOptions(false)
  }

  const handleOptionClick = (
    event: React.MouseEvent | React.KeyboardEvent,
    option: IAcAutocompleteOption
  ) => {
    event.stopPropagation()
    setShowOptions(false)
    setQuery('')
    onChange(option)
    inputRef.current?.focus()
  }

  const handleFocusKeyDown = (
    event: React.KeyboardEvent,
    option?: IAcAutocompleteOption
  ) => {
    const activeElement = document.activeElement
    const optionFocussed = activeElement?.getAttribute('role') === 'option'
    let preventDefault = true

    switch (event.code) {
      case 'Space':
      case 'NumpadEnter':
      case 'Enter': {
        if (!showOptions) {
          setShowOptions(true)
        } else if (optionFocussed && option) {
          handleOptionClick(event, option)
        } else if (option) {
          // Enter pressed in search input with options available
          handleOptionClick(event, option)
        }
        break
      }
      case 'Escape':
      case 'Tab': {
        setShowOptions(false)
        preventDefault = false
        break
      }
      case 'ArrowUp':
      case 'ArrowDown': {
        if (!dropdownRef.current) return

        const optionsContainer = dropdownRef.current.querySelector(
          '[class*="ac-autocomplete-options-wrapper"]'
        )
        if (!optionsContainer) return

        const allOptions = Array.from(
          optionsContainer.querySelectorAll('[role="option"]')
        )
        if (allOptions.length === 0) return

        if (!showOptions) {
          setShowOptions(true)
          return
        }

        const currentIndex = allOptions.indexOf(activeElement as Element)
        let nextIndex: number

        if (event.code === 'ArrowDown') {
          nextIndex =
            currentIndex < 0 ? 0 : Math.min(currentIndex + 1, allOptions.length - 1)
        } else {
          nextIndex = currentIndex <= 0 ? 0 : currentIndex - 1
        }

        const nextOption = allOptions[nextIndex] as HTMLElement
        nextOption?.focus()
        break
      }
      default: {
        preventDefault = false
      }
    }
    if (preventDefault) event.preventDefault()
  }

  const renderOptions = useCallback(() => {
    const filterOptions = query
      ? options.filter(singleOption =>
          JSON.stringify(singleOption[labelKey])
            .toLowerCase()
            .includes(query.toLowerCase())
        )
      : options

    return filterOptions.map(singleOption =>
      renderOption ? (
        <span
          key={singleOption[valueKey]}
          role="option"
          className={styles['ac-autocomplete-options-option']}
          onClick={event => handleOptionClick(event, singleOption)}
          onKeyDown={event => handleFocusKeyDown(event, singleOption)}
          tabIndex={0}>
          {renderOption(singleOption)}
        </span>
      ) : (
        <span
          role="option"
          className={clsx(
            styles['ac-autocomplete-options-option'],
            styles['ac-autocomplete-options-option--default']
          )}
          key={singleOption[valueKey]}
          onClick={event => handleOptionClick(event, singleOption)}
          onKeyDown={event => handleFocusKeyDown(event, singleOption)}
          tabIndex={0}>
          <AcTypography size="sm" weight="light" element="span">
            {singleOption[labelKey] as string}
          </AcTypography>
        </span>
      )
    )
  }, [options, query, renderOption, labelKey, valueKey])

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
      const optionsContainer = dropdownRef.current?.querySelector(
        '[class*="ac-autocomplete-options-wrapper"]'
      )
      const firstOption = optionsContainer?.querySelector(
        '[role="option"]'
      ) as HTMLElement
      if (firstOption && showOptions) {
        e.preventDefault()
        firstOption.focus()
      } else if (!showOptions) {
        setShowOptions(true)
      }
    } else if (e.code === 'Enter' && showOptions) {
      const filterOptions = query
        ? options.filter(singleOption =>
            JSON.stringify(singleOption[labelKey])
              .toLowerCase()
              .includes(query.toLowerCase())
          )
        : options
      if (filterOptions.length > 0) {
        e.preventDefault()
        handleOptionClick(e as any, filterOptions[0])
      }
    } else if (e.code === 'Escape') {
      setShowOptions(false)
    }
  }

  const renderEndAdornment = () => {
    return (
      <AcIcon
        icon={ICONS.ANGLE_DOWN}
        className={clsx(
          styles['ac-autocomplete-icon'],
          showOptions && styles['ac-autocomplete-icon--opened']
        )}
      />
    )
  }

  return (
    <div
      ref={dropdownRef}
      onClick={() => {
        if (!disabled && !loading) {
          setShowOptions(!showOptions)
        }
      }}
      className={clsx(
        styles['ac-autocomplete'],
        hint && styles['ac-autocomplete--has-hint'],
        className
      )}>
      <AcInputBase
        disabled={disabled}
        hint={hint}
        loading={loading}
        endAdornment={renderEndAdornment()}
        error={error}>
        <input
          ref={inputRef}
          name={name}
          id={id}
          type="text"
          placeholder={placeholder}
          className={styles['ac-autocomplete-input']}
          value={query}
          disabled={disabled || loading}
          onClick={e => {
            e.stopPropagation()
            setShowOptions(true)
          }}
          onChange={e => {
            const newQuery = e.target.value
            setQuery(newQuery)

            // Clear existing timer
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current)
            }

            // Set new timer for debounced search
            debounceTimerRef.current = setTimeout(() => {
              onSearch(newQuery)
            }, debounceMs)
          }}
          onKeyDown={handleInputKeyDown}
        />
      </AcInputBase>
      {showOptions && !loading && options.length > 0 && (
        <AcCard
          className={styles['ac-autocomplete-options-wrapper']}
          elevation="sm"
          padding={8}>
          {renderOptions()}
        </AcCard>
      )}
    </div>
  )
}
